/**
 * LegacyUrlRedirect
 *
 * Handles legacy flat URL patterns that were in the old sitemap
 * (/zateplenie-bratislava, /bratislava, /pezinok, etc.) by client-side
 * navigating to the canonical /sluzby/:service/:city URL, or to the
 * homepage when only a city is specified. Falls through to the given
 * `fallback` component for anything that doesn't match a known pattern.
 *
 * This is a best-effort SEO consolidation shim — a proper 301 would
 * need ingress-level rewrite, but React Navigate + <link rel=canonical>
 * on the destination pages is enough for Google to consolidate signals.
 */
import { Navigate, useLocation } from "react-router-dom";

// All city slugs we now serve — matches CITIES in backend/routes/cities.py.
const CITY_SLUGS = new Set([
  // Bratislava
  "bratislava", "bratislava-stare-mesto", "bratislava-ruzinov", "bratislava-nove-mesto",
  "bratislava-petrzalka", "bratislava-raca", "bratislava-vajnory", "bratislava-vrakuna",
  "bratislava-podunajske-biskupice", "bratislava-karlova-ves", "bratislava-dubravka",
  "bratislava-devin", "bratislava-devinska-nova-ves", "bratislava-lamac",
  "bratislava-zahorska-bystrica", "bratislava-jarovce", "bratislava-rusovce", "bratislava-cunovo",
  // Senec
  "senec", "ivanka-pri-dunaji", "bernolakovo", "dunajska-luzna", "malinovo", "miloslavov",
  "kralova-pri-senci", "chorvatsky-grob", "rovinka", "hviezdoslavov", "kvetoslavov", "hruba-borsa",
  // Pezinok
  "pezinok", "modra", "svaty-jur", "slovensky-grob", "vinicne", "vinosady", "senkvice",
  // Malacky
  "malacky", "stupava", "lozorno", "zohor", "plavecky-stvrtok", "vysoka-pri-morave",
  "marianka", "zahorska-ves",
  // Dunajská Streda
  "dunajska-streda", "samorin", "velky-meder", "gabcikovo", "baka", "horna-poton", "lehnice",
  // Galanta
  "galanta", "sered", "sladkovicovo", "trstice", "velky-grob", "velka-maca",
  // Šaľa
  "sala", "diakovce", "trnovec-nad-vahom",
  // Trnava + Piešťany
  "trnava", "piestany",
]);

// Known service prefixes → canonical /sluzby/:slug URL.
const SERVICE_MAP = {
  "zateplenie": "zateplenie",
  "holodomy": "holodomy",
  "rekonstrukcie": "rekonstrukcie",
  "stavby-na-kluc": "stavby-na-kluc",
  "sadrokarton": "sadrokarton",
  "zamkova-dlazba": "zamkova-dlazba",
  "bazeny": "bazeny",
  "zaklady": "zaklady",
  "betonove-ploty": "ploty",
  "ploty": "ploty",
  "strechy": "strechy",
  "murovanie": "murovanie",
  "omietky": "omietky",
  "rekonstrukcia-kupelne": "rekonstrukcia-kupelne",
  "bytove-jadra": "bytove-jadra",
  "garaze": "garaze",
  "altanky": "altanky",
};

export default function LegacyUrlRedirect({ fallback: Fallback }) {
  const { pathname } = useLocation();
  const clean = pathname.replace(/^\/+|\/+$/g, "");

  if (!clean) {
    return Fallback ? <Fallback /> : null;
  }

  // Pattern 1: <service>-<city>  e.g. "zateplenie-bratislava"
  for (const [prefix, canonicalService] of Object.entries(SERVICE_MAP)) {
    const p = `${prefix}-`;
    if (clean.startsWith(p)) {
      const city = clean.slice(p.length);
      if (CITY_SLUGS.has(city)) {
        // Services with dedicated city landing pages (backend generates AI copy).
        const supported = ["zateplenie", "holodomy", "rekonstrukcie", "zamkova-dlazba", "ploty"];
        if (supported.includes(canonicalService)) {
          return <Navigate to={`/sluzby/${canonicalService}/${city}`} replace />;
        }
        return <Navigate to={`/sluzby/${canonicalService}`} replace />;
      }
    }
  }

  // Pattern 2: <city> only — redirect to homepage to avoid thin content.
  if (CITY_SLUGS.has(clean)) {
    return <Navigate to="/" replace />;
  }

  // Pattern 3: bratislava/<district> — redirect to homepage.
  if (clean.startsWith("bratislava/")) {
    return <Navigate to="/" replace />;
  }

  return Fallback ? <Fallback /> : null;
}
